import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ContentRail = ({ title, items, showProgress = false, emptyMessage = "Nenhum conteúdo disponível" }) => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    const checkScrollButtons = () => {
      setCanScrollLeft(container?.scrollLeft > 0);
      setCanScrollRight(
        container?.scrollLeft < container?.scrollWidth - container?.clientWidth
      );
    };

    checkScrollButtons();
    container?.addEventListener('scroll', checkScrollButtons);
    
    return () => container?.removeEventListener('scroll', checkScrollButtons);
  }, [items]);

  const scroll = (direction) => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    const scrollAmount = 320; // Width of card + gap
    const newScrollLeft = direction === 'left' 
      ? container?.scrollLeft - scrollAmount
      : container?.scrollLeft + scrollAmount;

    container?.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e?.pageX - scrollContainerRef?.current?.offsetLeft);
    setScrollLeft(scrollContainerRef?.current?.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e?.preventDefault();
    const x = e?.pageX - scrollContainerRef?.current?.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleCardClick = (item) => {
    if (isDragging) return;
    
    if (item?.type === 'course') {
      // Navigate to course module with the first available module
      if (item.moduleSlug) {
        navigate(`/course/formacao-lideres-mentores/module/${item.moduleSlug}`);
      } else {
        navigate('/course/formacao-lideres-mentores');
      }
    } else if (item?.type === 'lesson') {
      // Navigate to module instead of specific lesson
      navigate(`/course/formacao-lideres-mentores/module/modulo-1-formacao-de-lideres-e-mentores`);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      available: { text: 'Disponível', className: 'bg-success text-success-foreground' },
      'coming-soon': { text: 'Em Breve', className: 'bg-warning text-warning-foreground' },
      completed: { text: 'Concluído', className: 'bg-accent text-accent-foreground' },
      'in-progress': { text: 'Em Andamento', className: 'bg-secondary text-secondary-foreground' }
    };
    
    return badges?.[status] || badges?.available;
  };

  if (!items || items?.length === 0) {
    return (
      <section className="mb-12">
        <h2 className="text-2xl font-heading font-semibold text-foreground mb-6">{title}</h2>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-40 h-40 bg-muted rounded-full flex items-center justify-center mb-4">
            <Icon name="BookOpen" size={80} className="text-muted-foreground" />
          </div>
          <p className="text-muted-foreground font-caption font-size-4xl">{emptyMessage}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-12">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-heading font-semibold text-foreground">{title}</h2>
        <Button
          variant="ghost"
          iconName="ChevronRight"
          iconPosition="right"
          className="text-muted-foreground hover:text-foreground"
        >
          Ver Todos
        </Button>
      </div>
      {/* Content Rail */}
      <div className="relative group">
        {/* Left Scroll Button */}
        {canScrollLeft && (
          <Button
            variant="default"
            onClick={() => scroll('left')}
            iconName="ChevronLeft"
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full shadow-tier-2 opacity-0 group-hover:opacity-100 transition-opacity"
          />
        )}

        {/* Right Scroll Button */}
        {canScrollRight && (
          <Button
            variant="default"
            onClick={() => scroll('right')}
            iconName="ChevronRight"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full shadow-tier-2 opacity-0 group-hover:opacity-100 transition-opacity"
          />
        )}

        {/* Scrollable Container */}
        <div
          ref={scrollContainerRef}
          className="flex space-x-4 overflow-x-auto scrollbar-hide pb-4 cursor-grab active:cursor-grabbing"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          {items?.map((item) => {
            const badge = getStatusBadge(item?.status);
            
            return (
              <div
                key={item?.id}
                className="flex-shrink-0 w-72 bg-card rounded-lg overflow-hidden shadow-tier-1 hover:shadow-tier-2 transition-all duration-300 cursor-pointer group/card"
                onClick={() => handleCardClick(item)}
              >
                {/* Thumbnail */}
                <div className="relative aspect-video overflow-hidden">
                  <Image
                    src={item?.thumbnail}
                    alt={item?.title}
                    className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-300"
                  />
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover/card:bg-black/20 transition-colors duration-300"></div>
                  
                  {/* Play Button */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/card:opacity-100 transition-opacity duration-300">
                    <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center backdrop-blur-sm">
                      <Icon name="Play" size={20} className="text-primary ml-1" />
                    </div>
                  </div>

                  {/* Duration Badge */}
                  {item?.duration && (
                    <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 text-white text-xs rounded backdrop-blur-sm">
                      {item?.duration}
                    </div>
                  )}

                  {/* Status Badge */}
                  <div className="absolute top-2 left-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${badge?.className}`}>
                      {badge?.text}
                    </span>
                  </div>

                  {/* Progress Bar */}
                  {showProgress && item?.progress > 0 && (
                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/30">
                      <div 
                        className="h-full bg-secondary transition-all duration-500"
                        style={{ width: `${item?.progress}%` }}
                      ></div>
                    </div>
                  )}
                </div>
                {/* Content */}
                <div className="p-4">
                  <h3 className="font-heading font-semibold text-card-foreground mb-2 line-clamp-2 group-hover/card:text-primary transition-colors">
                    {item?.title}
                  </h3>
                  
                  {item?.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2 font-caption">
                      {item?.description}
                    </p>
                  )}

                  {/* Metadata */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground font-caption">
                    <div className="flex items-center space-x-3">
                      {item?.instructor && (
                        <div className="flex items-center space-x-1">
                          <Icon name="User" size={12} />
                          <span>{item?.instructor}</span>
                        </div>
                      )}
                      {item?.lessons && (
                        <div className="flex items-center space-x-1">
                          <Icon name="BookOpen" size={12} />
                          <span>{item?.lessons} lições</span>
                        </div>
                      )}
                    </div>
                    
                    {showProgress && item?.progress > 0 && (
                      <span className="text-secondary font-medium">
                        {item?.progress}%
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ContentRail;